import React from "react";
import { ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";
import { haptic } from "@/lib/haptics";
import {
  SheetRoot,
  SheetShell,
  SheetTitle,
  SheetDescription,
  SHEET_ROW_CLASS,
} from "@/components/mobile/SheetShell";

/**
 * Generic native-style action sheet, controlled from the outside.
 *
 *   <ActionSheet open onOpenChange title="Cadrage" subtitle="12 oct → 15 oct">
 *     <ActionRow icon={Pencil} onSelect={…}>Modifier</ActionRow>
 *     <ActionRow icon={Trash2} destructive onSelect={…}>Supprimer</ActionRow>
 *   </ActionSheet>
 */
export default function ActionSheet({
  open,
  onOpenChange,
  title,
  subtitle,
  header,
  children,
  testId,
}) {
  return (
    <SheetRoot open={open} onOpenChange={onOpenChange}>
      <SheetShell showClose={false} data-testid={testId} bodyClassName="px-2 pt-0">
        <SheetTitle className="sr-only">{title || "Menu"}</SheetTitle>
        <SheetDescription className="sr-only">{subtitle || title || "Menu"}</SheetDescription>
        {header ? (
          header
        ) : title ? (
          <div className="px-3 pb-2 pt-1">
            <div className="truncate font-display text-lg font-black tracking-tight">{title}</div>
            {subtitle && <div className="mt-0.5 truncate text-xs text-neutral-500">{subtitle}</div>}
          </div>
        ) : null}
        <div role="menu" className="pb-1">
          {children}
        </div>
      </SheetShell>
    </SheetRoot>
  );
}

/** One tappable row. `onSelect` fires, then the parent decides whether to close. */
export function ActionRow({
  icon: Icon,
  children,
  onSelect,
  destructive = false,
  accent = false,
  disabled = false,
  chevron = false,
  trailing,
  testId,
  className,
}) {
  return (
    <button
      type="button"
      role="menuitem"
      data-testid={testId}
      disabled={disabled}
      onClick={(e) => {
        haptic("tick");
        onSelect?.(e);
      }}
      className={cn(
        SHEET_ROW_CLASS,
        destructive && "text-red-600 [&>svg]:text-red-500",
        accent && "text-[#E05236] [&>svg]:text-[#E05236]",
        className
      )}
    >
      {Icon && <Icon strokeWidth={1.75} />}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing}
      {chevron && <ChevronRight className="ml-2 !mr-0 !size-4 !text-neutral-300" />}
    </button>
  );
}

export function ActionDivider() {
  return <div role="separator" className="mx-3 my-1 h-px bg-stone-100" />;
}

export function ActionLabel({ children }) {
  return (
    <div className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[0.15em] text-neutral-400">
      {children}
    </div>
  );
}
